/**
 * Whether a notification interrupts somebody — the one rule (plan.md step 61, §37).
 *
 * Every notification is WRITTEN (the feed is the record a phone that was off
 * in a tent can still read). This decides only whether it is also PUSHED —
 * rings a phone — and records why not:
 *
 * - CRITICAL always rings: an emergency is never held by quiet hours, a
 *   frequency setting, a muted festival or a cap (§37.34, §37.44).
 * - a muted festival rings for nothing else;
 * - frequency OFF rings for nothing else; IMPORTANT ONLY for HIGH and above;
 * - quiet hours hold the rest — unless the person chose "a live festival
 *   overrides quiet hours" and the notice is about a festival on right now
 *   (§37.45: the person stays in control);
 * - and a daily cap keeps a busy day from becoming spam (§37.43, §37.71).
 *
 * Pure: the API decides with it, and the settings screen explains with it.
 */
import { zonedParts } from './time.js';

export const NOTIFICATION_PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'CRITICAL'] as const;
export type NotificationPriority = (typeof NOTIFICATION_PRIORITIES)[number];

export const NOTIFICATION_FREQUENCIES = ['REAL_TIME', 'IMPORTANT_ONLY', 'OFF'] as const;
export type NotificationFrequency = (typeof NOTIFICATION_FREQUENCIES)[number];

export interface NotificationSettings {
  frequency: NotificationFrequency;
  /** `HH:MM` in `quietTimeZone`; both or neither. */
  quietFrom: string | null;
  quietTo: string | null;
  /** The person's own zone (their phone's), so 23:00 means their 23:00. */
  quietTimeZone: string | null;
  /** A festival on now overrides quiet hours for its own notices (§37.45). */
  festivalOverridesQuiet: boolean;
  /** Festivals whose notices go to the feed only. */
  mutedFestivals: string[];
  /**
   * "You're near the Dome — now: …" on the phone, while the app is open
   * (`proximity.ts`). ON for everyone by default (the user, 2026-10-07), at
   * `stageAlertRadiusM`; switched off or moved in the account's settings.
   */
  stageAlerts: boolean;
  /** One of `PROXIMITY_RADII`; 100 m unless chosen. */
  stageAlertRadiusM: number;
  /**
   * Per-rave overrides of the kind switches (§37.17): festival id → topic
   * (`FESTIVAL_TOPICS`) or kind → `false`. Only `false` is stored — a rave
   * follows the account's own switches unless told otherwise.
   */
  festivalTopics: Record<string, Record<string, false>>;
  /** Artist ids whose booking, set reminder and full set are not told (§37.20). */
  mutedArtists: string[];
  /** One email a day of what is still unread (§37.41–43). Opt-in. */
  dailySummary: boolean;
}

/** The stage-alert distances on offer, metres — `PROXIMITY_RADII`, here too for the reader below. */
export const STAGE_ALERT_RADII = [25, 50, 100, 250] as const;
export const DEFAULT_STAGE_ALERT_RADIUS_M = 100;

export const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
  frequency: 'REAL_TIME', quietFrom: null, quietTo: null, quietTimeZone: null,
  festivalOverridesQuiet: true, mutedFestivals: [],
  stageAlerts: true, stageAlertRadiusM: DEFAULT_STAGE_ALERT_RADIUS_M,
  festivalTopics: {}, mutedArtists: [], dailySummary: false,
};

/** Pushes per person per day, beyond which only CRITICAL rings. */
export const DAILY_PUSH_CAP = 25;

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Read stored settings, keeping only what is valid — a bad value is the default, never an error. */
export function readNotificationSettings(stored: unknown): NotificationSettings {
  const o = (stored && typeof stored === 'object' ? stored : {}) as Record<string, unknown>;
  const quiet = typeof o.quietFrom === 'string' && HHMM.test(o.quietFrom) && typeof o.quietTo === 'string' && HHMM.test(o.quietTo);
  return {
    frequency: (NOTIFICATION_FREQUENCIES as readonly unknown[]).includes(o.frequency) ? o.frequency as NotificationFrequency : 'REAL_TIME',
    quietFrom: quiet ? o.quietFrom as string : null,
    quietTo: quiet ? o.quietTo as string : null,
    quietTimeZone: typeof o.quietTimeZone === 'string' && o.quietTimeZone.length < 64 ? o.quietTimeZone : null,
    festivalOverridesQuiet: o.festivalOverridesQuiet !== false,
    mutedFestivals: Array.isArray(o.mutedFestivals) ? o.mutedFestivals.filter((x): x is string => typeof x === 'string').slice(0, 200) : [],
    stageAlerts: o.stageAlerts !== false,
    stageAlertRadiusM: (STAGE_ALERT_RADII as readonly unknown[]).includes(o.stageAlertRadiusM)
      ? o.stageAlertRadiusM as number : DEFAULT_STAGE_ALERT_RADIUS_M,
    festivalTopics: readFestivalTopics(o.festivalTopics),
    mutedArtists: Array.isArray(o.mutedArtists)
      ? [...new Set(o.mutedArtists.filter((x): x is string => typeof x === 'string' && x.length > 0 && x.length <= 100))].slice(0, 500)
      : [],
    dailySummary: o.dailySummary === true,
  };
}

/** A topic key or a kind's own name (`lineup.changed`). */
const TOPIC_KEY = /^[a-z_]+(\.[a-z_]+)?$/;

function readFestivalTopics(stored: unknown): Record<string, Record<string, false>> {
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return {};
  const out: Record<string, Record<string, false>> = {};
  for (const [festivalId, topics] of Object.entries(stored as Record<string, unknown>).slice(0, 200)) {
    if (!festivalId || festivalId.length > 100 || !topics || typeof topics !== 'object' || Array.isArray(topics)) continue;
    const off: Record<string, false> = {};
    for (const [key, value] of Object.entries(topics as Record<string, unknown>)) {
      if (value === false && key.length <= 60 && TOPIC_KEY.test(key)
        && ((FESTIVAL_TOPICS as readonly string[]).includes(key) || key.includes('.'))) off[key] = false;
    }
    if (Object.keys(off).length > 0) out[festivalId] = off;
  }
  return out;
}

/* ------------------------------------------------ category and source --- */

/** What a notice is about (§37.75) — derived per kind, an announcement may name its own. */
export const NOTIFICATION_CATEGORIES = [
  'lineup', 'announcement', 'reminder', 'social', 'rides', 'payment', 'account', 'system', 'safety',
] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

/** Who made it (§37.75): an admin, MyRave itself, Claude (only through an approved proposal), or a data update. */
export const NOTIFICATION_SOURCES = ['ADMIN', 'SYSTEM', 'AI', 'DATA_UPDATE'] as const;
export type NotificationSource = (typeof NOTIFICATION_SOURCES)[number];

/** Per channel (§37.60): what happened to its email or text message. */
const DELIVERY_STATUSES = ['NONE', 'QUEUED', 'SENT', 'FAILED', 'SKIPPED'] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

/**
 * Everything new about an artist somebody FOLLOWS (step 76) — following is the
 * opt-in: booked, a set's time published, another set, a set moved, cancelled,
 * a full set up. Never a reminder: those belong to Save this set (the user,
 * 2026-10-28: "there is no remind on following an artist"). "Mute this artist"
 * holds exactly these.
 */
export const ARTIST_FOLLOW_KINDS = [
  'artist.booked', 'artist.set_time', 'artist.set_added', 'artist.moved', 'artist.cancelled', 'artist.full_set',
] as const;
export type ArtistFollowKind = (typeof ARTIST_FOLLOW_KINDS)[number];
const FOLLOW = new Set<string>(ARTIST_FOLLOW_KINDS);
export const isArtistFollowKind = (kind: string): kind is ArtistFollowKind => FOLLOW.has(kind);

export function notificationCategory(kind: string, data?: Record<string, unknown>): NotificationCategory {
  if (typeof data?.category === 'string' && (NOTIFICATION_CATEGORIES as readonly string[]).includes(data.category)) {
    return data.category as NotificationCategory;
  }
  if (kind === 'emergency') return 'safety';
  if (kind === 'announcement.posted') return 'announcement';
  if (kind === 'act.starting') return 'reminder';
  if (kind === 'lineup.changed' || isArtistFollowKind(kind) || kind === 'recording.added') return 'lineup';
  if (kind.startsWith('ride.')) return 'rides';
  if (kind.startsWith('payment.')) return 'payment';
  if (kind === 'match.new' || kind === 'message.received' || kind === 'dating.warning' || kind === 'photo.matched') return 'social';
  if (kind.startsWith('admin_invite.') || kind.startsWith('rave.')) return 'account';
  return 'system';
}

export function notificationSource(kind: string, data?: Record<string, unknown>): NotificationSource {
  if (typeof data?.source === 'string' && (NOTIFICATION_SOURCES as readonly string[]).includes(data.source)) {
    return data.source as NotificationSource;
  }
  if (kind === 'lineup.changed' || isArtistFollowKind(kind) || kind === 'recording.added') return 'DATA_UPDATE';
  if (kind === 'announcement.posted' || kind === 'emergency' || kind === 'dating.warning' || kind === 'ride.warning'
    || kind === 'rave.suspended' || kind === 'rave.unsuspended' || kind === 'payment.refund_declined') return 'ADMIN';
  return 'SYSTEM';
}

/* ------------------------------------------ per-rave topics, artists --- */

/** The switches a person has per rave (§37.17), beside the account's own. */
export const FESTIVAL_TOPICS = ['lineup', 'announcements', 'reminders', 'recordings'] as const;
export type FestivalTopic = (typeof FESTIVAL_TOPICS)[number];

export function festivalTopicOf(kind: string): FestivalTopic | null {
  if (kind === 'lineup.changed' || isArtistFollowKind(kind)) return 'lineup';
  if (kind === 'announcement.posted') return 'announcements';
  if (kind === 'act.starting') return 'reminders';
  if (kind === 'recording.added') return 'recordings';
  return null;
}

/**
 * "Mute this artist" (§37.20) holds the artist-follow notices and nothing else
 * (step 76): a saved set's reminder and its full set are Save this set's, and
 * muting an artist's NEWS leaves them alone. It held `act.starting` and
 * `recording.added` until 2026-10-28.
 */
const ARTIST_KINDS = FOLLOW;

/**
 * Whether a per-rave topic or a muted artist holds this notice — no row, no
 * push, no email, as a switched-off kind. Never the emergency or anything
 * CRITICAL, and never an IMPORTANT announcement: a person who switched a
 * rave's announcements off still hears the gates moved.
 */
export function heldByScope(
  s: Pick<NotificationSettings, 'festivalTopics' | 'mutedArtists'>,
  n: { kind: string; festivalId?: string | null; artistId?: string | null; priority?: NotificationPriority },
): 'FESTIVAL_TOPIC' | 'MUTED_ARTIST' | null {
  if (n.kind === 'emergency' || n.priority === 'CRITICAL') return null;
  // An IMPORTANT announcement is not "news" — it still comes. A set reminder
  // is HIGH too, and the person switched it off for this rave on purpose.
  if (n.kind === 'announcement.posted' && n.priority === 'HIGH') return null;
  if (n.artistId && ARTIST_KINDS.has(n.kind) && s.mutedArtists.includes(n.artistId)) return 'MUTED_ARTIST';
  const off = n.festivalId ? s.festivalTopics[n.festivalId] : undefined;
  if (off) {
    const topic = festivalTopicOf(n.kind);
    if ((topic && off[topic] === false) || off[n.kind] === false) return 'FESTIVAL_TOPIC';
  }
  return null;
}

const RANK: Record<NotificationPriority, number> = { LOW: 0, NORMAL: 1, HIGH: 2, CRITICAL: 3 };

function isQuiet(now: Date, s: NotificationSettings): boolean {
  if (!s.quietFrom || !s.quietTo) return false;
  let p;
  try { p = zonedParts(now, s.quietTimeZone ?? 'UTC'); } catch { p = zonedParts(now, 'UTC'); }
  const t = p.hour * 60 + p.minute;
  const [fh, fm] = s.quietFrom.split(':').map(Number);
  const [th, tm] = s.quietTo.split(':').map(Number);
  const a = fh! * 60 + fm!;
  const b = th! * 60 + tm!;
  return a <= b ? t >= a && t < b : t >= a || t < b;
}

export type PushResult = 'SENT' | 'QUIET' | 'CAPPED' | 'FREQUENCY' | 'MUTED_FESTIVAL';

export function pushDecision(input: {
  settings: NotificationSettings;
  priority: NotificationPriority;
  now: Date;
  festivalId?: string | null;
  /** The festival this is about is on right now. */
  festivalLive?: boolean;
  pushesToday: number;
  /** The platform's daily cap (a console setting, 2026-10-09); `DAILY_PUSH_CAP` when not given. */
  cap?: number;
}): PushResult {
  const { settings: s, priority } = input;
  if (priority === 'CRITICAL') return 'SENT';
  if (input.festivalId && s.mutedFestivals.includes(input.festivalId)) return 'MUTED_FESTIVAL';
  if (s.frequency === 'OFF') return 'FREQUENCY';
  if (s.frequency === 'IMPORTANT_ONLY' && RANK[priority] < RANK.HIGH) return 'FREQUENCY';
  if (isQuiet(input.now, s) && !(s.festivalOverridesQuiet && input.festivalLive)) return 'QUIET';
  if (input.pushesToday >= (input.cap ?? DAILY_PUSH_CAP)) return 'CAPPED';
  return 'SENT';
}

/** The priority a kind carries unless its sender says otherwise (§37.5). */
export function defaultPriority(kind: string, data?: Record<string, unknown>): NotificationPriority {
  if (kind === 'emergency') return 'CRITICAL';
  // A moderator's warning is about the account's standing (the user, 2026-10-07).
  // A dispute pauses access the person paid for (2026-10-07).
  if (kind === 'act.starting' || kind === 'payment.failed' || kind === 'ride.accepted' || kind === 'ride.cancelled'
    || kind === 'dating.warning' || kind === 'ride.warning' || kind === 'payment.disputed'
    // A rave this person runs was suspended by the platform (2026-10-10).
    || kind === 'rave.suspended') return 'HIGH';
  if (kind === 'announcement.posted') return data?.level === 'IMPORTANT' ? 'HIGH' : 'NORMAL';
  if (kind === 'photo.matched' || isArtistFollowKind(kind) || kind === 'recording.added' || kind === 'match.new'
    // MyRave's own release notes (§37.36): news, never an interruption.
    || kind === 'release.published'
    // After a rave: rate MyRave, support it (step 108) — a question, never an interruption.
    || kind === 'myrave.ask') return 'LOW';
  return 'NORMAL';
}
